/**
 * Part-to-whole families: pie and donut.
 *
 * Slices carry a surface-coloured border, which is the gap between adjacent
 * fills that the mark spec requires as secondary encoding.
 */
import type { EChartsOption } from 'echarts';
import { chartTokens, type ChartMedia, type ChartMode } from '@/tokens/chart';
import type { ChartConfig, ChartRow } from '../types';
import { baseOption, seriesColor } from './shared';

const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

export function buildPartToWholeOption(
  config: ChartConfig,
  data: readonly ChartRow[],
  mode: ChartMode,
  media: ChartMedia,
): EChartsOption {
  const tokens = chartTokens[mode];
  const entry = config.series.find((s) => !s.hidden);
  const nameField = entry?.fields.x;
  const valueField = entry?.fields.y ?? entry?.fields.value;

  const usable =
    entry &&
    nameField &&
    valueField &&
    data.length > 0 &&
    nameField in data[0] &&
    valueField in data[0];

  if (!usable) {
    if (entry && data.length > 0) {
      console.warn(
        `[octopus-ui/chart] series "${entry.name}" references fields not present in the data; ` +
          'nothing to render.',
      );
    }
    return { ...baseOption(config, mode, media), series: [] } as EChartsOption;
  }

  return {
    ...baseOption(config, mode, media),
    series: [
      {
        id: entry.id,
        name: entry.name,
        type: 'pie',
        radius: config.chartType === 'donut' ? ['48%', '70%'] : '65%',
        itemStyle: { borderColor: tokens.surface, borderWidth: 2 },
        label: { color: tokens.ink.label },
        labelLine: { lineStyle: { color: tokens.ink.axis } },
        data: data.map((row, index) => ({
          name: String(row[nameField]),
          value: toNumber(row[valueField]),
          itemStyle: { color: seriesColor(index, entry, mode) },
        })),
      },
    ],
  } as EChartsOption;
}
